import type { BoardState } from './board';
import type { Command, PlayerState } from './types';
import type { Rng } from './rng';
import type { AiTierName } from './config';
import { bfsNextDir, manhattan, neighborId, DIRS } from './grid';
import { passable } from './rules';

interface TierParams {
  reactionDelayMs: number;
  knowledgeAccuracy: number; // P(belief matches truth)
  riskAppetite: number; // 0..1; higher = more willing to chase fenced answers
  /** Board-start head start for the HUMAN: the AI's first act waits this long. Deterministic constant. */
  startGraceMs: number;
}

export const AI_TIERS: Record<AiTierName, TierParams> = {
  // G1 fun rebalance (2026-06-22, panel-authorized): the rival must read as a fallible player on a
  // topic the player does NOT know, not an oracle. Lowering knowledgeAccuracy makes it confidently
  // eat decoys more often (visible, beatable mistakes) and slows its points-race so a blank-slate
  // player can win a fair share. Reaction/risk nudged so the opening boards breathe and the rival
  // over-commits to fenced answers (more human over-reach). Savant stays a real threat.
  // 2026-06-26 CHOMP-only reset: removing auto-eat also removes incidental "free" corrects during
  // routing, so the rival backs off slightly to keep unknown-topic play from collapsing.
  // 2026-07-02 owner-playtest retune ("the AI is too fast/hard starting out"): a human reads labels at
  // 2-4s per decision — rookie at 800ms felt like racing an oracle. Rookie slowed to a learnable
  // opener + every tier gets a board-start grace so the PLAYER always moves first; savant's in-round
  // pace is untouched (the endgame threat stands). A/B-simmed vs the prior tiers (see determinism.md).
  // 2026-07-04 FACE&FURY V2-R1 RIVAL PRESENCE: an honest blank-slate run was a 2.6× blowout and the
  // rival's HUD chip sat near 0 — the rival lacked late-game presence. Fix = strengthen the MID/LATE
  // tiers so boards 3-5 (rungs 2-4 = sharp/sharp/savant) stay contested to the wire, while the EARLY
  // tier (rookie, rungs 0-1) is left untouched so the opener stays merciful (owner "too hard" balance
  // preserved). PvP-symmetric: these are the same belief=truth-XOR-noise params a network player would
  // face — NO ground truth is added; knowledgeAccuracy is the noise level, not an oracle read. Sharp:
  // faster cadence (620→380ms) + a shorter opener grace (1400→1000ms, human still moves first by ~1s) +
  // fewer decoy mistakes (0.72→0.80) + more Gauntlet appetite (0.55→0.68). Savant: a touch faster
  // (300→245ms), sharper (0.84→0.87), bolder (0.8→0.85) — still ~13% fallible, so it visibly eats decoys.
  // Measured (decoupled per-rung blank-slate probe, topics×seeds): rung 2 human-win 65→58%, rung 3
  // 60→54%, rung 4 53→41%; rival mean score up ~+20..+60/board on rungs 2-4; official 115×50 aggregate
  // win 51.7→44.6% (inside the threadrate 40-62% guard, ~4.6pp margin) with median threads/run 2.0 held.
  // Rungs 0-1 numbers are byte-identical (rookie untouched). Determinism CONTRACT unchanged (constants
  // only; no new RNG site/tick step/salt/event) but same-seed OUTCOMES shift → bv=6→7, goldens re-stamped.
  rookie: { reactionDelayMs: 1450, knowledgeAccuracy: 0.56, riskAppetite: 0.3, startGraceMs: 2600 },
  sharp: { reactionDelayMs: 380, knowledgeAccuracy: 0.8, riskAppetite: 0.68, startGraceMs: 1000 },
  savant: { reactionDelayMs: 245, knowledgeAccuracy: 0.87, riskAppetite: 0.85, startGraceMs: 300 },
};

/**
 * The opponent gets NO ground truth: per cell it believes (truth XOR noise), governed by
 * knowledgeAccuracy. So it sometimes confidently munches a decoy and loses points — the same
 * mistake a human makes. That fallibility is what makes it beatable and feel like a player.
 */
export class AIController {
  readonly playerId: number;
  private tier: TierParams;
  private rng: Rng;
  private belief = new Map<number, boolean>();
  private lastActMs = -Infinity;
  private target: number | null = null;
  private speedMul = 1; // <1 = act faster (rubber-band when behind); >1 = ease off when ahead
  private nudgesUsed = 0;
  private lastNudgeMs = -Infinity;
  private static readonly MAX_NUDGES = 8;

  /** Rubber-band the AI's effort (not its knowledge): scales the reaction delay. Clamped. */
  setSpeedMul(m: number): void {
    this.speedMul = Math.max(0.4, Math.min(2, m));
  }

  /**
   * Bounded, throttled rubber-band on KNOWLEDGE: nudge ONE belief toward truth (catch up when behind)
   * or toward a mistake (ease off when ahead). Capped per board (MAX_NUDGES) + throttled (1.2s) so the
   * AI never becomes omniscient and can't be handed a win it didn't earn.
   */
  nudgeBelief(state: BoardState, towardTruth: boolean, nowMs: number): void {
    if (nowMs < this.lastNudgeMs + 1200 || this.nudgesUsed >= AIController.MAX_NUDGES) return;
    const eligible = state.cells.filter((c) => {
      if (c.kind === 'wall' || c.eaten) return false;
      const believesTruth = this.belief.get(c.id) === (c.kind === 'correct');
      return towardTruth ? !believesTruth : believesTruth;
    });
    if (eligible.length === 0) return;
    const c = this.rng.pick(eligible);
    const truth = c.kind === 'correct';
    this.belief.set(c.id, towardTruth ? truth : !truth);
    this.nudgesUsed += 1;
    this.lastNudgeMs = nowMs;
  }

  constructor(playerId: number, tierName: AiTierName, rng: Rng) {
    this.playerId = playerId;
    this.tier = AI_TIERS[tierName];
    this.rng = rng;
  }

  onBoardStart(state: BoardState): void {
    this.belief.clear();
    for (const c of state.cells) {
      if (c.kind === 'wall') continue;
      const truth = c.kind === 'correct';
      const knows = this.rng.chance(this.tier.knowledgeAccuracy);
      this.belief.set(c.id, knows ? truth : !truth);
    }
    this.target = null;
    // First act lands at startGraceMs (decide() gates on lastActMs + reactionDelay·speedMul): the
    // human always gets the opening move — the single biggest "too fast" owner-playtest complaint.
    // Caveat (review 2026-07-02): sim's rubber-band runs pre-decide, so on boards 2+ where the AI
    // is already 250+ BEHIND, speedMul 0.5 compresses the grace (rookie 2600→1875ms effective).
    // Acceptable: the AI only gets its head start shaved when it's losing the match.
    this.lastActMs = this.tier.startGraceMs - this.tier.reactionDelayMs;
    this.speedMul = 1;
    this.nudgesUsed = 0;
    this.lastNudgeMs = -Infinity;
  }

  decide(state: BoardState, me: PlayerState, nowMs: number): Command | null {
    if (!me.alive || nowMs < me.stunnedUntilMs) return null;
    if (nowMs < this.lastActMs + this.tier.reactionDelayMs * this.speedMul) return null;
    this.lastActMs = nowMs;

    const here = state.cells[me.pos];
    if (!here.eaten && here.kind !== 'wall' && this.belief.get(me.pos) === true) {
      this.target = null;
      return { type: 'munch' };
    }

    if (this.target === null || state.cells[this.target].eaten || this.belief.get(this.target) !== true) {
      this.target = this.chooseTarget(state, me);
    }
    if (this.target === null) return { type: 'wait' };

    const dir = bfsNextDir(me.pos, this.target, state.rows, state.cols, (id) => passable(state, id));
    if (!dir) {
      this.target = null;
      return { type: 'wait' };
    }
    return { type: 'move', dir };
  }

  private chooseTarget(state: BoardState, me: PlayerState): number | null {
    let best: number | null = null;
    let bestScore = Infinity;
    for (const c of state.cells) {
      if (c.eaten || c.kind === 'wall') continue;
      if (this.belief.get(c.id) !== true) continue;
      if (c.armed && !c.eaten) continue;
      const dist = manhattan(me.pos, c.id, state.cols);
      const fenced = this.fencedness(state, c.id);
      const score = dist + (1 - this.tier.riskAppetite) * fenced * 2;
      if (score < bestScore) {
        bestScore = score;
        best = c.id;
      }
    }
    return best;
  }

  private fencedness(state: BoardState, id: number): number {
    let n = 0;
    for (const d of DIRS) {
      const nb = neighborId(id, d, state.rows, state.cols);
      if (nb === null) {
        n++;
        continue;
      }
      const c = state.cells[nb];
      if (c.kind === 'wall' || (c.kind === 'decoy' && !c.eaten)) n++;
    }
    return n;
  }
}
